import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'app/store';
import { getContentByUrl } from 'features/content/services';
import { setContent } from 'features/content/reducer';
import { fetcher } from 'app/http';
import { IUser } from './types';

export interface CoreState {
  settings: {
    [locale: string]: any;
  };
  masterLanguage: string;
  languagePath: string;
  currentMarket: string;
  user: IUser | null;
  dataLoading: boolean;
}

const initialState: CoreState = {
  settings: {},
  masterLanguage: '',
  languagePath: '/',
  currentMarket: '',
  user: null,
  dataLoading: false,
};

export const getCoreData = (): AppThunk => async (dispatch, getState) => {
  try {
    const { languagePath } = getState().core;
    const response = await getContentByUrl(languagePath);
    if (response?.data) {
      dispatch(setContent({ [languagePath]: response.data }));
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Thunk used to remove the root page data from store
 * @returns
 */
export const removeCoreData = (): AppThunk => async (dispatch, getState) => {
  const { languagePath } = getState().core;
  dispatch(setContent({ [languagePath]: null }));
};

export const logOut = (): AppThunk => async (dispatch, getState) => {
  const currentState = getState();
  const languagePath = currentState.core?.languagePath;
  const siteSettings = currentState.content[languagePath];
  try {
    await fetcher.get(
      siteSettings?.LayoutModel?.SiteNavigationService?.LogoutPage?.Uri,
    );
  } catch (error) {
    console.error(error);
  } finally {
    await dispatch(setUser(null));
    window.location.href = languagePath; 
  }
};

export const logOutRedirectsToLogin = (): AppThunk => async (dispatch, getState) => {
  const currentState = getState();
  const languagePath = currentState.core?.languagePath;
  const siteSettings = currentState.content[languagePath];
  try {
    await fetcher.get(
      siteSettings?.LayoutModel?.SiteNavigationService?.LogoutPage?.Uri,
    );
  } catch (error) {
    console.error(error);
  } finally {
    await dispatch(setUser(null));
    window.location.href =  siteSettings?.LayoutModel?.SiteNavigationService?.LoginNewPage?.Uri; 
  }
};

export const logOutRedirectsToResetLogin = (): AppThunk => async (dispatch, getState) => {
  const currentState = getState();
  const languagePath = currentState.core?.languagePath;
  const siteSettings = currentState.content[languagePath];
  try {
    await fetcher.get(
      siteSettings?.LayoutModel?.SiteNavigationService?.LogoutPage?.Uri,
    );
  } catch (error) {
    console.error(error);
  } finally {
    await dispatch(setUser(null));
    window.location.href =  `${siteSettings?.LayoutModel?.SiteNavigationService?.LoginNewPage?.Uri}?redirectToReset=true`;
  }
};

export const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    setContent(state, action: PayloadAction<any>) {
      return { ...state.settings, ...action.payload };
    },
    setMasterLanguage(state, action: PayloadAction<any>) {
      state.masterLanguage = action.payload;
    },
    setLanguage(state, action: PayloadAction<any>) {
      state.languagePath = action.payload;
    },
    setCurrentMarket(state, action: PayloadAction<any>) {
      state.currentMarket = action.payload;
    },
    setUser(state, action: PayloadAction<any>) {
      state.user = action.payload;
    },
    setDataLoading(state, action: PayloadAction<any>) {
      state.dataLoading = action.payload;
    },
  },
});

export const {
  setLanguage,
  setMasterLanguage,
  setCurrentMarket,
  setUser,
  setDataLoading,
} = coreSlice.actions;

export default coreSlice.reducer;
